import { useEffect, useMemo, useState } from 'react';

import DefaultLayout from '../../layout/DefaultLayout';
import BreadcrumbTwo from '../../components/Breadcrumbs/BreadcrumbTwo';
import SectionWrapper from '../../components/SectionWrapper/SectionWrapper';
import CompactSelectField from '../../components/Forms/Input/CompactSelectField';
import SearchInputField from '../../components/Forms/Input/SearchInputField';
import Checkbox from '../../components/Forms/Input/Checkbox';
import UserService from '../../services/UserService';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useUserPermissionOverrides } from '../../hooks/useUserPermissionOverrides';
import { roleLabel } from '../../constants/roleLabels';
import { moduleLabel, actionLabel } from '../../constants/permissionModules';
import type { PermissionCatalogItem } from '../../store/slices/rolePermissions/rolePermissionsSlice';

interface UserSearchResult {
  id: number;
  username: string;
  email: string;
  roles?: { name: string }[];
}

const sortedUnique = (arr: string[]) => Array.from(new Set(arr)).sort();

/**
 * 🔐 Panel administrativo: permisos por usuario (excepciones sobre el rol).
 *
 * Endpoint solo-SUPPORT. El rol sigue siendo la base: acá se ajustan
 * excepciones puntuales para UN usuario, sin tocar a los demás con el mismo
 * rol. Misma UX que el panel de roles (grupo por módulo, filtro, búsqueda),
 * más un buscador de usuario arriba.
 */
const UserPermissionsPage = () => {
  const {
    selectedUser,
    basePermissions,
    effectivePermissions,
    catalog,
    getUserPermissions,
    updateUserPermissions,
    togglePermission,
    setPermissionList,
    clearSelection,
  } = useUserPermissionOverrides();

  const [userQuery, setUserQuery] = useState('');
  const debouncedUserQuery = useDebouncedValue(userQuery, 350);
  const [userResults, setUserResults] = useState<UserSearchResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [moduleFilter, setModuleFilter] = useState<string>('ALL');
  const [search, setSearch] = useState<string>('');
  const [baseline, setBaseline] = useState<string[]>([]);

  // Buscar usuarios a medida que se escribe.
  useEffect(() => {
    const term = debouncedUserQuery.trim();
    if (term.length < 2) {
      setUserResults([]);
      return;
    }
    let cancelled = false;
    setSearchingUsers(true);
    UserService.searchUsersWithPagination(term, 0, 10)
      .then((response: any) => {
        if (cancelled) return;
        const content = response?.data?.content ?? [];
        setUserResults(content);
      })
      .catch(() => {
        if (!cancelled) setUserResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearchingUsers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedUserQuery]);

  // Snapshot de referencia cada vez que cambia el usuario seleccionado.
  useEffect(() => {
    setBaseline(sortedUnique(effectivePermissions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id]);

  const current = useMemo(() => new Set(effectivePermissions), [effectivePermissions]);
  const base = useMemo(() => new Set(basePermissions), [basePermissions]);

  const dirty = useMemo(() => {
    const now = sortedUnique(effectivePermissions);
    return now.length !== baseline.length || now.some((p, i) => p !== baseline[i]);
  }, [effectivePermissions, baseline]);

  const modules = useMemo(() => sortedUnique(catalog.map((c) => c.module)), [catalog]);

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const out: Record<string, PermissionCatalogItem[]> = {};
    catalog.forEach((item) => {
      if (moduleFilter !== 'ALL' && item.module !== moduleFilter) return;
      if (
        term &&
        !item.name.toLowerCase().includes(term) &&
        !(item.label ?? '').toLowerCase().includes(term) &&
        !(item.description ?? '').toLowerCase().includes(term)
      )
        return;
      (out[item.module] ??= []).push(item);
    });
    Object.values(out).forEach((list) => list.sort((a, b) => a.action.localeCompare(b.action)));
    return out;
  }, [catalog, moduleFilter, search]);

  const groupModules = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const totalAssignable = catalog.filter((c) => !c.supportOnly).length;

  const moduleOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Todos los módulos' },
      ...modules.map((m) => ({ value: m, label: moduleLabel(m) })),
    ],
    [modules],
  );

  const selectUser = (user: UserSearchResult) => {
    setUserQuery('');
    setUserResults([]);
    setShowResults(false);
    getUserPermissions(user.id);
  };

  const toggleModule = (mod: string, check: boolean) => {
    const modPerms = catalog.filter((c) => c.module === mod && !c.supportOnly).map((c) => c.name);
    const next = new Set(effectivePermissions);
    if (check) modPerms.forEach((p) => next.add(p));
    else modPerms.forEach((p) => next.delete(p));
    setPermissionList(Array.from(next));
  };

  const handleSave = async () => {
    const ok = await updateUserPermissions();
    if (ok) setBaseline(sortedUnique(effectivePermissions));
  };

  const handleReset = () => setPermissionList(baseline);

  return (
    <DefaultLayout>
      <BreadcrumbTwo />
      <SectionWrapper>
        <div className="space-y-6 p-6">
          <div>
            <h2 className="text-title-sm font-semibold text-black dark:text-white">
              Permisos por usuario
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-body dark:text-bodydark">
              Buscá un usuario para ajustarle excepciones puntuales sobre lo que ya le da su rol.
              Los cambios acá solo afectan a{' '}
              <span className="font-semibold text-black dark:text-white">ese usuario</span>, no al
              resto de usuarios con el mismo rol.
            </p>
          </div>

          {/* Buscador de usuario */}
          <div className="relative max-w-md">
            <label className="mb-2 block text-sm font-medium text-black dark:text-white">
              Usuario
            </label>
            <SearchInputField
              name="userSearch"
              label=""
              value={userQuery}
              onChange={(e) => {
                setUserQuery(e.target.value);
                setShowResults(true);
              }}
              onFocus={() => setShowResults(true)}
              placeholder="Buscar por nombre de usuario o correo…"
              size="sm"
              withMargin={false}
            />
            {showResults && userQuery.trim().length >= 2 && (
              <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                {searchingUsers && (
                  <div className="px-4 py-3 text-sm text-body dark:text-bodydark">Buscando…</div>
                )}
                {!searchingUsers && userResults.length === 0 && (
                  <div className="px-4 py-3 text-sm text-body dark:text-bodydark">
                    Sin resultados.
                  </div>
                )}
                {!searchingUsers &&
                  userResults.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => selectUser(u)}
                      className="flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left transition-colors hover:bg-gray-1 dark:hover:bg-meta-4/40"
                    >
                      <span className="font-medium text-black dark:text-white">{u.username}</span>
                      <span className="text-xs text-body dark:text-bodydark">
                        {u.email} ·{' '}
                        {(u.roles ?? []).map((r) => roleLabel(r.name)).join(', ') || 'Sin rol'}
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </div>

          {!selectedUser && (
            <p className="py-8 text-center text-sm text-body dark:text-bodydark">
              Buscá y seleccioná un usuario para ver y ajustar sus permisos.
            </p>
          )}

          {selectedUser && (
            <>
              {/* Ficha del usuario seleccionado */}
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-stroke bg-gray-2 px-4 py-3 dark:border-strokedark dark:bg-meta-4">
                <div>
                  <p className="font-semibold text-black dark:text-white">
                    {selectedUser.username}
                  </p>
                  <p className="text-sm text-body dark:text-bodydark">
                    {selectedUser.email} ·{' '}
                    {selectedUser.roles.map((r) => roleLabel(r)).join(', ') || 'Sin rol'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Cambiar usuario
                </button>
              </div>

              {/* Toolbar */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,16rem)_1fr_auto] lg:items-end">
                <div>
                  <label className="mb-2 block text-sm font-medium text-black dark:text-white">
                    Módulo
                  </label>
                  <CompactSelectField
                    name="module"
                    value={moduleFilter}
                    options={moduleOptions}
                    onChange={(e) => setModuleFilter(String(e.target.value))}
                    iconKey="filter"
                  />
                </div>

                <SearchInputField
                  name="permissionSearch"
                  label="Buscar"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="nombre o descripción…"
                  size="sm"
                  withMargin={false}
                />

                <div className="rounded-lg border border-stroke bg-gray-2 px-4 py-2.5 text-center dark:border-strokedark dark:bg-meta-4 lg:mb-[1px]">
                  <span className="block text-lg font-semibold text-black dark:text-white">
                    {current.size}
                    <span className="text-body dark:text-bodydark"> / {totalAssignable}</span>
                  </span>
                  <span className="text-xs text-body dark:text-bodydark">permisos</span>
                </div>
              </div>

              {/* Grid por módulo */}
              <div className="space-y-5">
                {groupModules.length === 0 && (
                  <p className="py-8 text-center text-sm text-body dark:text-bodydark">
                    No hay permisos que coincidan con el filtro.
                  </p>
                )}

                {groupModules.map((mod) => {
                  const items = grouped[mod];
                  const assignable = items.filter((i) => !i.supportOnly);
                  const allChecked = assignable.length > 0 && assignable.every((i) => current.has(i.name));
                  const checkedInModule = items.filter((i) => current.has(i.name)).length;

                  return (
                    <div
                      key={mod}
                      className="overflow-hidden rounded-lg border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark"
                    >
                      <div className="flex items-center justify-between gap-4 border-b border-stroke bg-gray-2 px-4 py-3 dark:border-strokedark dark:bg-meta-4">
                        <h3 className="flex items-center gap-2 font-semibold text-black dark:text-white">
                          {moduleLabel(mod)}
                          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-normal text-body dark:bg-boxdark dark:text-bodydark">
                            {checkedInModule}/{items.length}
                          </span>
                        </h3>
                        {assignable.length > 0 && (
                          <Checkbox
                            label="Marcar todo el módulo"
                            checked={allChecked}
                            onChange={() => toggleModule(mod, !allChecked)}
                            className="text-xs"
                          />
                        )}
                      </div>

                      <ul className="divide-y divide-stroke dark:divide-strokedark">
                        {items.map((item) => {
                          const checked = current.has(item.name);
                          const isFromRole = base.has(item.name);
                          const overridden = checked !== isFromRole;
                          const disabled = item.supportOnly;
                          return (
                            <li
                              key={item.name}
                              className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-1 dark:hover:bg-meta-4/40"
                            >
                              <div className="pt-0.5">
                                <Checkbox
                                  id={`user-perm-${item.name}`}
                                  checked={checked}
                                  disabled={disabled}
                                  onChange={() => togglePermission(item.name)}
                                />
                              </div>

                              <label
                                htmlFor={`user-perm-${item.name}`}
                                className={`min-w-0 flex-1 ${disabled ? '' : 'cursor-pointer'}`}
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-black dark:text-white">
                                    {item.label || item.name}
                                  </span>
                                  <span className="rounded-full bg-gray-2 px-2 py-0.5 text-[11px] text-body dark:bg-meta-4 dark:text-bodydark">
                                    {actionLabel(item.action)}
                                  </span>
                                  {item.supportOnly && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">
                                      🔒 Solo Soporte
                                    </span>
                                  )}
                                  {overridden && (
                                    <span
                                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                        checked
                                          ? 'bg-success/10 text-success'
                                          : 'bg-danger/10 text-danger'
                                      }`}
                                    >
                                      {checked ? '+ Otorgado a este usuario' : '− Quitado a este usuario'}
                                    </span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="mt-0.5 text-sm text-body dark:text-bodydark">
                                    {item.description}
                                  </p>
                                )}
                                <p className="mt-0.5 font-mono text-[11px] text-body/70 dark:text-bodydark/70">
                                  {item.name}
                                </p>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {selectedUser && (
          <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t border-stroke bg-white px-6 py-4 dark:border-strokedark dark:bg-boxdark">
            {dirty && (
              <span className="mr-auto inline-flex items-center gap-2 text-sm font-medium text-warning">
                <span className="h-2 w-2 rounded-full bg-warning" />
                Cambios sin guardar
              </span>
            )}
            <button
              type="button"
              onClick={handleReset}
              disabled={!dirty}
              className="rounded-lg border border-stroke px-4 py-2 text-sm font-medium text-black transition hover:bg-gray-1 disabled:opacity-40 dark:border-strokedark dark:text-white dark:hover:bg-meta-4"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty}
              className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:opacity-40"
            >
              Guardar {selectedUser.username}
            </button>
          </div>
        )}
      </SectionWrapper>
    </DefaultLayout>
  );
};

export default UserPermissionsPage;
